import { MainContainer } from "@touche-lts/panel-ui/dist";
import {
  DialogPopupHandler,
  Header,
  LoadingSpinner,
  SideMenu
} from "components";
import { observer } from "mobx-react-lite";
import { history } from "mobx-stores";
import { useRootStore } from "mobx-stores/store-provider";
import {
  AddStorePage,
  AddUserPage,
  DashboardPage,
  HelpPage,
  LegalEditorPage,
  LegalTablePage,
  LoginPage,
  ProductsPage,
  SignUpPage,
  StatisticsPage,
  StoresPage,
  UserManagementPage,
  UserProfilePage,
  ViewStorePage,
  ActionHistoryPage
} from "pages";
import { ShopifyLoginPage, ShopifySignupPage } from "pages/shopify";
import { CustomersPage } from "pages/customers";
import { CustomerProfilePage } from "pages/customers/customer-profile-page";
import { AddDepartmentsPage } from "pages/departments/add-department";
import { CategoryDepartmentsPage } from "pages/departments/category-department";
import { DepartmentsPage } from "pages/departments/departments-page";
import { useEffect } from "react";
import { withAlert } from "react-alert";
import { Router as ReactRouter, Route, Switch } from "react-router";
import { SettingsPage } from "../pages/settings";
import { PrivacyPolicyPage } from "../pages/privacy-policy";
import { PrivateRoute } from "./private-route";
import { RouterProps } from "./router.types";
import { AllowedSsoEmailsPage } from "../pages/settings/allowed-sso-emails-table/allowed-sso-emails-table"
import { UserRoleName } from "models";

const SUPER_ADMIN_ONLY = [UserRoleName.SUPER_ADMIN];

const RouterComponent = (props: RouterProps) => {
  const { alert } = props;

  const {
    generalStore: { alert: alertData },
    routingStore: {
      location: { pathname },
    },
  } = useRootStore();

  useEffect(() => {
    if (alertData.counter > 0) {
      alert[alertData.type](alertData.message);
    }
    // eslint-disable-next-line
  }, [alertData.counter]);

  const visibleHeader = pathname !== "/login" &&
    !pathname.startsWith("/signup") &&
    !pathname.startsWith("/shopify") &&
    !pathname.startsWith("/privacy-policy");
  const visibleSideMenu = visibleHeader;

  return (
    <ReactRouter history={history}>
      {visibleHeader && <Header />}
      <MainContainer>
        {visibleSideMenu && <SideMenu />}
        <Switch>
          {/* Public routes */}
          <Route path="/login" component={LoginPage} />
          <Route path="/signup/:code" component={SignUpPage} />
          <Route path="/shopify/login" component={ShopifyLoginPage} />
          <Route path="/shopify/signup" component={ShopifySignupPage} />
          <Route exact path="/privacy-policy" component={PrivacyPolicyPage} />

          {/* Private routes — super admin only */}
          <PrivateRoute exact path="/" component={DashboardPage} allowedRoles={SUPER_ADMIN_ONLY} />
          <PrivateRoute exact path="/dashboard" component={DashboardPage} allowedRoles={SUPER_ADMIN_ONLY} />

          <PrivateRoute
            exact
            path="/user-management"
            component={UserManagementPage}
            allowedRoles={SUPER_ADMIN_ONLY}
          />
          <PrivateRoute
            exact
            path="/departments"
            component={DepartmentsPage}
            allowedRoles={SUPER_ADMIN_ONLY}
          />
          <PrivateRoute
            exact
            path="/departments/add"
            component={AddDepartmentsPage}
            allowedRoles={SUPER_ADMIN_ONLY}
          />
          <PrivateRoute
            exact
            path="/departments/:departmentId"
            component={AddDepartmentsPage}
            allowedRoles={SUPER_ADMIN_ONLY}
          />
          <PrivateRoute
            exact
            path="/departments/:departmentId/:categoryId"
            component={CategoryDepartmentsPage}
            allowedRoles={SUPER_ADMIN_ONLY}
          />
          <PrivateRoute
            exact
            path="/departments/:departmentId/addCategory"
            component={CategoryDepartmentsPage}
            allowedRoles={SUPER_ADMIN_ONLY}
          />
          <PrivateRoute
            exact
            path="/user-management/add-user"
            component={AddUserPage}
            allowedRoles={SUPER_ADMIN_ONLY}
          />
          <PrivateRoute
            exact
            path="/user-management/:userId"
            component={UserProfilePage}
            allowedRoles={SUPER_ADMIN_ONLY}
          />
          <PrivateRoute
            exact
            path={"/customers"}
            component={CustomersPage}
            allowedRoles={SUPER_ADMIN_ONLY}
          />
          <PrivateRoute
            exact
            path={"/customers/:userId"}
            component={CustomerProfilePage}
            allowedRoles={SUPER_ADMIN_ONLY}
          />
          <PrivateRoute
            exact
            path={"/legal"}
            component={LegalTablePage}
            allowedRoles={SUPER_ADMIN_ONLY}
          />
          <PrivateRoute
            exact
            path={"/legal/editor"}
            component={LegalEditorPage}
            allowedRoles={SUPER_ADMIN_ONLY}
          />
          <PrivateRoute
            exact
            path={"/legal/editor/:legalId"}
            component={LegalEditorPage}
            allowedRoles={SUPER_ADMIN_ONLY}
          />
          <PrivateRoute
            exact
            path={"/allowed-email"}
            component={AllowedSsoEmailsPage}
            allowedRoles={SUPER_ADMIN_ONLY}
          />

          {/* Private routes — all authenticated users */}
          <PrivateRoute exact path="/stores" component={StoresPage} />
          <PrivateRoute exact path="/stores/add" component={AddStorePage} />
          <PrivateRoute exact path="/stores/:storeId" component={ViewStorePage} />
          <PrivateRoute exact path="/products" component={ProductsPage} />
          <PrivateRoute exact path={"/statistics"} component={StatisticsPage} />
          <PrivateRoute exact path="/actionhistory" component={ActionHistoryPage} />
          <PrivateRoute exact path={"/help"} component={HelpPage} />
          <PrivateRoute exact path={"/settings"} component={SettingsPage} />
        </Switch>
      </MainContainer>
      <LoadingSpinner />
      <DialogPopupHandler />
    </ReactRouter>
  );
};

const Router = withAlert()(observer(RouterComponent));
export { Router };
